import _ from 'underscore';
import React, {
  createRef, useCallback, useEffect, useRef, useState,
} from 'react';

import { ToolsDialogConfig } from './types';
import { ToolsMenu } from './tools-menu';
import { ToolsList } from './tools-list';
import { areConfigsGroupsTheSame } from './utils';
import { useContentStyles } from './styles';

const SCROLL_INTO_VIEW_DELAY = 250;

export const ToolsContent: React.FC<{
  config: ToolsDialogConfig;
  searchValue: string;
}> = ({ config, searchValue }) => {
  const classes = useContentStyles();
  const groupRefs = _.reduce(config.groups, (prev, group) => _.extend(prev, { [group.key]: createRef() }), {});

  const [selectedGroup, setSelectedGroup] = useState<string>(_.get(config, ['groups', '0', 'key']));

  const onGroupSelect = useCallback((groupKey) => {
    groupRefs[groupKey].current.scrollIntoView({ behavior: 'smooth' });

    // ScrollIntoView is async, so we need to wait a bit before setting the selected group
    // to avoid the menu flickering
    setTimeout(() => setSelectedGroup(groupKey), SCROLL_INTO_VIEW_DELAY);
  }, [groupRefs]);

  const onFirstVisibleGroupChange = useCallback((groupKey) => {
    setSelectedGroup(groupKey);
  }, []);

  const previousConfigRef = useRef<ToolsDialogConfig>();

  // When the config changes, we need to reset the selected group to the first one
  useEffect(() => {
    if (!areConfigsGroupsTheSame(previousConfigRef.current, config)) {
      const groupKey = _.get(config, ['groups', '0', 'key']);
      setSelectedGroup(groupKey);
    }

    previousConfigRef.current = config;
  }, [config]);

  return (
    <div className={classes.root}>
      <ToolsMenu
        groups={config.groups}
        selectedGroup={selectedGroup}
        onGroupSelect={onGroupSelect}
      />
      <ToolsList
        groups={config.groups}
        groupRefs={groupRefs}
        searchValue={searchValue}
        onFirstVisibleGroupChange={onFirstVisibleGroupChange}
      />
    </div>
  );
};
